import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import useCompanyContext from '../../hooks/useCompanyContext';

import SmartTable from '../../components/Smart/SmartTable';
import SmartPagination from '../../components/Smart/SmartPagination';
import API from '../../api/axios';

const PAGE_SIZE = 12;

const normalizeRow = (item) => {
    return {
        id:
            item.id ??
            item.invoiceId ??
            item.number ??
            `${item.supplierId || 'all'}-${item.invoiceDate || item.period || Math.random()}`,
        period: item.period ?? item.invoiceDate ?? '-',
        supplierId: item.supplierId ?? item.partnerId ?? null,
        supplierName: item.supplierName ?? item.partnerName ?? item.partner?.name ?? '-',
        number: item.number ?? item.name ?? '-',
        amountTotal: Number(item.amountTotal ?? item.total ?? item.amount_total ?? 0),
        deposit: Number(item.deposit ?? item.rmbDeposit ?? item.rmb_diposit ?? 0),
        paymentState: item.paymentState ?? item.payment_state ?? null,
        moveType: item.moveType ?? item.move_type ?? null,
    };
};

const AccountingsList = () => {
    const {
        currentCompanyID,
        hasSpecificCompany,
        isAllCompanies,
        isAdmin,
        buildCompanyUrl,
    } = useCompanyContext();

    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState({
        amountTotal: 0,
        deposit: 0,
    });

    const [page, setPage] = useState({
        page_number: 1,
        total_pages: 1,
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const now = new Date();
    const currentYear = now.getFullYear();

    const [filters, setFilters] = useState({
        year: currentYear,
        month: 0,
    });

    /**
     * 统一使用 useCompanyContext：
     *
     * - 具体 company：传 supplierId
     * - ADMIN + All companies：不传 supplierId
     * - 普通用户：currentCompanyID 为其固定 company UUID
     */
    const shouldShowAllSuppliers = isAdmin && isAllCompanies;

    const buildQueryParams = useCallback(() => {
        const p = new URLSearchParams();

        if (filters.year) {
            p.set('year', String(filters.year));
        }

        if (filters.month !== undefined && filters.month !== null) {
            p.set('month', String(filters.month));
        }

        if (hasSpecificCompany) {
            p.set('supplierId', String(currentCompanyID));
        }

        return p.toString();
    }, [
        filters.year,
        filters.month,
        hasSpecificCompany,
        currentCompanyID,
    ]);

    const fetchAccountings = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const qs = buildQueryParams();

            /**
             * 前端分页：
             * 后端返回全部数据，所以这里不传 page
             */
            const url = qs
                ? `/accounting/summary?${qs}`
                : '/accounting/summary';

            const { data } = await API.get(url);

            const list = data?.data || [];
            const normalizedRows = list.map(normalizeRow);

            const totalPages = Math.max(
                1,
                Math.ceil(normalizedRows.length / PAGE_SIZE),
            );

            setRows(normalizedRows);

            setTotals({
                amountTotal: Number(
                    data?.totals?.amountTotal ??
                    data?.totals?.total ??
                    0,
                ),
                deposit: Number(data?.totals?.deposit ?? 0),
            });

            setPage((prev) => ({
                ...prev,
                page_number: 1,
                total_pages: totalPages,
            }));
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                e?.message ||
                'Failed to load accountings',
            );

            setRows([]);

            setTotals({
                amountTotal: 0,
                deposit: 0,
            });

            setPage({
                page_number: 1,
                total_pages: 1,
            });
        } finally {
            setLoading(false);
        }
    }, [buildQueryParams]);

    useEffect(() => {
        fetchAccountings();
    }, [fetchAccountings]);

    const pagedRows = useMemo(() => {
        const currentPage = page.page_number || 1;
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        const endIndex = startIndex + PAGE_SIZE;

        return rows.slice(startIndex, endIndex);
    }, [rows, page.page_number]);

    const columns = [
        {
            key: 'period',
            label: 'Fecha',
            render: (item) => item.period || '-',
        },
        ...(shouldShowAllSuppliers
            ? [
                {
                    key: 'supplierName',
                    label: 'Proveedor',
                    render: (item) => (
                        <div>
                            <div>{item.supplierName || '-'}</div>
                            {item.supplierId ? (
                                <small className="text-muted">
                                    ID: {item.supplierId}
                                </small>
                            ) : null}
                        </div>
                    ),
                },
            ]
            : []),
        {
            key: 'number',
            label: 'Factura',
            render: (item) => item.number || '-',
        },
        {
            key: 'deposit',
            label: 'Depósito',
            render: (item) =>
                `${Number(item.deposit || 0).toFixed(2)} €`,
        },
        {
            key: 'amountTotal',
            label: 'Total Factura',
            render: (item) =>
                `${Number(item.amountTotal || 0).toFixed(2)} €`,
        },
        {
            key: 'paymentState',
            label: 'Estado',
            render: (item) =>
                item.paymentState ? (
                    <Badge
                        bg={
                            item.paymentState === 'paid'
                                ? 'success'
                                : 'secondary'
                        }
                    >
                        {item.paymentState}
                    </Badge>
                ) : (
                    <span className="text-muted">-</span>
                ),
        },
        {
            key: 'moveType',
            label: 'Tipo',
            render: (item) =>
                item.moveType ? (
                    <Badge
                        bg={
                            item.moveType === 'out_refund'
                                ? 'warning'
                                : 'info'
                        }
                    >
                        {item.moveType}
                    </Badge>
                ) : (
                    <span className="text-muted">-</span>
                ),
        },
    ];

    const yearOptions = [];

    for (let y = 2018; y <= currentYear; y += 1) {
        yearOptions.push(y);
    }

    return (
        <Container fluid className="d-flex flex-column">
            <Row className="mb-4">
                <Col sm={8}>
                    <span className="fw-semibold">Contabilidad</span>
                </Col>

                <Col sm={4} className="text-end">
                    <span className="fw-semibold">
                        <NavLink
                            to={buildCompanyUrl('/dashboard')}
                            className="block-link"
                        >
                            Dashboard
                        </NavLink>{' '}
                        - Contabilidad
                    </span>
                </Col>
            </Row>

            <Row>
                <Col sm={12}>
                    <Card className="h-100 p-2">
                        <Card.Body className="px-2 pt-2 pb-5">
                            <Row className="mb-3 align-items-end">
                                <Col md={2}>
                                    <Form.Label>Year</Form.Label>

                                    <Form.Select
                                        value={filters.year}
                                        onChange={(e) =>
                                            setFilters((prev) => ({
                                                ...prev,
                                                year: Number(
                                                    e.target.value,
                                                ),
                                            }))
                                        }
                                    >
                                        {yearOptions.map((year) => (
                                            <option
                                                key={year}
                                                value={year}
                                            >
                                                {year}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>

                                <Col md={2}>
                                    <Form.Label>Month</Form.Label>

                                    <Form.Select
                                        value={filters.month}
                                        onChange={(e) =>
                                            setFilters((prev) => ({
                                                ...prev,
                                                month: Number(
                                                    e.target.value,
                                                ),
                                            }))
                                        }
                                    >
                                        <option value={0}>all</option>

                                        {Array.from(
                                            { length: 12 },
                                            (_, i) => i + 1,
                                        ).map((month) => (
                                            <option
                                                key={month}
                                                value={month}
                                            >
                                                {month}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>

                                <Col md={4}>
                                    <Form.Label>Supplier</Form.Label>

                                    <div className="form-control bg-light">
                                        {shouldShowAllSuppliers ? (
                                            <span>Todos</span>
                                        ) : (
                                            <span>
                                                Supplier ID:{' '}
                                                {hasSpecificCompany
                                                    ? currentCompanyID
                                                    : '-'}
                                            </span>
                                        )}
                                    </div>
                                </Col>

                                <Col md={4} className="text-end">
                                    <div className="d-flex justify-content-end gap-3">
                                        <div>
                                            <small className="text-muted d-block">
                                                Total Factura
                                            </small>

                                            <strong>
                                                {totals.amountTotal.toFixed(2)} €
                                            </strong>
                                        </div>

                                        <div>
                                            <small className="text-muted d-block">
                                                Depósito
                                            </small>

                                            <strong>
                                                {totals.deposit.toFixed(2)} €
                                            </strong>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={12}>
                                    <SmartTable
                                        data={pagedRows}
                                        columns={columns}
                                        loading={loading}
                                        emptyState={
                                            <div className="text-center py-5 text-muted">
                                                {error ? (
                                                    <p>{error}</p>
                                                ) : (
                                                    <p>
                                                        No accounting records
                                                        found
                                                    </p>
                                                )}
                                            </div>
                                        }
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                <Col
                    md={12}
                    className="d-flex justify-content-end"
                >
                    <SmartPagination
                        page={page}
                        setPage={setPage}
                    />
                </Col>
            </Row>
        </Container>
    );
};

export default AccountingsList;